import React, { FunctionComponent, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Box } from '@fortress-technology-solutions/fortress-component-library/Atoms';

import ColumnHeader from '../../../components/ColumnHeader';
import { Row } from './Row';
import { Table } from 'react-bootstrap';

import { AFFORDABLE_PROGRAMS } from '../../../utils/affordable';
import useIsAffordableProperty from '../../../hooks/useIsAffordableProperty';
import usePropertyHasProgram from '../../../hooks/usePropertyHasProgram';

import type { Building, State } from '../types';

import messages from '../messages';
type Props = {
  buildings: Building[],
  updateBuilding: Function,
  states: State[],
  selectedProperty: Object,
  intl: Object,
  promptToaster: Function,
};

export const BuildingsTable = ({
  buildings,
  updateBuilding,
  states,
  selectedProperty,
  promptToaster,
  intl,
}: Props): FunctionComponent<Props> => {
  const { publicHousing, naur, bldgCertoccConstypeFields } = useFlags();
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const isAffordableProperty = useIsAffordableProperty(selectedProperty);
  const isRDProperty = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.RD,
  );
  const isLIHTCProperty = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.LIHTC,
  );
  const isBONDProperty = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.BOND,
  );
  const isPHProperty =
    usePropertyHasProgram(selectedProperty, AFFORDABLE_PROGRAMS.PH) &&
    publicHousing;
  const showFundingType =
    isAffordableProperty &&
    selectedProperty?.state?.id === 'US-CA' &&
    (isLIHTCProperty || isBONDProperty);
  const showCertOccConstructionType =
    bldgCertoccConstypeFields && isAffordableProperty && isLIHTCProperty;
  return (
    <Box
      sx={{
        overflowX: 'auto',
        '& > table': { width: 'max-content', minWidth: '100%' },
        '& th': { whiteSpace: 'nowrap' },
      }}
    >
      <Table striped className="table-prospects">
        <thead className="table-header">
          <tr>
            <ColumnHeader
              columnLabel={intl.formatMessage(messages.buildingNumber)}
            />
            <ColumnHeader
              columnLabel={intl.formatMessage(messages.buildingAddress)}
            />
            {isAffordableProperty && (
              <ColumnHeader
                columnLabel={intl.formatMessage(messages.xmlAltSiteName)}
              />
            )}
            <ColumnHeader
              columnLabel={intl.formatMessage(messages.buildingPISD)}
            />
            <ColumnHeader
              columnLabel={intl.formatMessage(messages.buildingBIN)}
            />
            {isAffordableProperty && isPHProperty && (
              <ColumnHeader
                columnLabel={intl.formatMessage(messages.phBuildingNumber)}
              />
            )}
            {isAffordableProperty && isPHProperty && (
              <ColumnHeader
                columnLabel={intl.formatMessage(messages.phBuildingEntrance)}
              />
            )}
            {isAffordableProperty && isRDProperty && (
              <ColumnHeader
                columnLabel={intl.formatMessage(messages.rdSiteId)}
              />
            )}
            {isAffordableProperty && isLIHTCProperty && (
              <ColumnHeader
                columnLabel={intl.formatMessage(messages.minimumSetAside)}
              />
            )}
            {isAffordableProperty && isLIHTCProperty && naur && (
              <ColumnHeader
                columnLabel={intl.formatMessage(
                  messages.applicableFractionUnit,
                )}
                tooltip={intl.formatMessage(
                  messages.applicableFractionUnitTooltip,
                )}
              />
            )}
            {isAffordableProperty && isLIHTCProperty && naur && (
              <ColumnHeader
                tooltip={intl.formatMessage(
                  messages.applicableFractionFloorSpaceTooltip,
                )}
                columnLabel={intl.formatMessage(
                  messages.applicableFractionFloorSpace,
                )}
              />
            )}
            {isAffordableProperty && (
              <ColumnHeader
                columnLabel={intl.formatMessage(messages.heraSpecial)}
              />
            )}
            {showFundingType && (
              <ColumnHeader
                columnLabel={intl.formatMessage(messages.fundingType)}
              />
            )}
            {showCertOccConstructionType && (
              <ColumnHeader
                columnLabel={intl.formatMessage(
                  messages.certificateOfOccupancyDate,
                )}
              />
            )}
            {showCertOccConstructionType && (
              <ColumnHeader
                columnLabel={intl.formatMessage(messages.constructionType)}
              />
            )}
            <ColumnHeader columnLabel={intl.formatMessage(messages.edit)} />
          </tr>
        </thead>
        <tbody>
          {buildings.map((building) => (
            <Row
              key={building.id}
              building={building}
              updateBuilding={updateBuilding}
              states={states}
              setSelectedBuilding={setSelectedBuilding}
              disabled={
                selectedBuilding ? building.id !== selectedBuilding : false
              }
              selectedProperty={selectedProperty}
              promptToaster={promptToaster}
              intl={intl}
              showFundingType={showFundingType}
            />
          ))}
        </tbody>
      </Table>
    </Box>
  );
};
